import type { ApiClient } from '../client';
import {
  DEFAULT_ANIMATION,
  parseAnimation,
  type AnimationConfig,
} from '@game-platform/pack-animation';
import {
  parseConstructorV2,
  type ConstructorValue,
} from '../../../features/design-constructor';
import { normalizeTemplate, type Template } from '../../theme';
import type { FundingMode } from '../../payments/types';
import { parseTrackingConfig, type TrackingConfig } from '../../tracking';
import { parseHttpUrl } from './parseHttpUrl';

interface RawResponse {
  template?: unknown;
  design_system?: unknown;
  disable_shipping?: unknown;
  funding_mode?: unknown;
  allow_withdrawals?: unknown;
  registration?: unknown;
  balance_keepalive_interval_sec?: unknown;
  lobby_url?: unknown;
  deposit_url?: unknown;
  demo_embed_key?: unknown;
  provably_fair?: unknown;
  tracking_pixels?: unknown;
}

export interface RegistrationInfo {
  mode: 'disabled' | 'required';
  registered: boolean;
}

export interface DesignSystemResult {
  template: Template;
  designSystem: ConstructorValue | null;
  /** How this template opens a pack (`design_system.animation`); the slot lane when unset. */
  animation: AnimationConfig;
  disableShipping: boolean;
  fundingMode: FundingMode;
  allowWithdrawals: boolean;
  registration: RegistrationInfo;
  /** Seconds between GET /balance keepalive polls; 0 = don't poll. */
  balanceKeepaliveSec: number;
  // Casino navigation targets from the launch payload (session metadata).
  // null when the operator didn't send them or sent a non-http(s) value.
  lobbyUrl: string | null;
  depositUrl: string | null;
  /**
   * The operator's public embed_key, sent only when some pack has demo mode
   * on. A session-token launch has no `?embed_key=` in its URL, so this is
   * the only way it can call the anonymous demo rip endpoint.
   */
  demoEmbedKey: string | null;
  /** The operator has provably fair on: buy with a ticket, show Verify. */
  provablyFair: boolean;
  /** Partner ad pixels; null when the operator has none or this player is excluded. */
  trackingPixels: TrackingConfig | null;
}

function parseFundingMode(v: unknown): FundingMode {
  return v === 'stripe' ? 'stripe' : 'seamless';
}

// The section rides inside design_system; `parseAnimation` turns anything
// unrecognisable into the slot lane, so a bad row never breaks the reveal.
function parseOpeningAnimation(designSystem: unknown): AnimationConfig {
  const section =
    designSystem && typeof designSystem === 'object'
      ? (designSystem as { animation?: unknown }).animation
      : undefined;
  return parseAnimation(section);
}

// Seamless casinos expire idle player sessions; the backend sends a positive
// interval only for operators that asked for a get_balance keepalive.
// Anything else (absent, non-numeric, ≤ 0) means "don't poll".
function parseKeepaliveSec(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0;
}

// Defensive: anything but an explicit `mode: 'required'` collapses to the
// legacy shape — `disabled` + `registered: true` keeps every existing
// operator path un-gated.
function parseRegistration(v: unknown): RegistrationInfo {
  if (v && typeof v === 'object' && (v as { mode?: unknown }).mode === 'required') {
    return { mode: 'required', registered: (v as { registered?: unknown }).registered === true };
  }
  return { mode: 'disabled', registered: true };
}

/**
 * Per-operator theme fetched once at iframe init from
 * GET /api/game_platform/iframe/v1/design_system. The backend now returns
 * v2-only shape ({ version: 2, palette, overrides, … }) plus a top-level
 * `template` string. Soft-fails: any non-OK / non-JSON response returns
 * `{ template: 'classic', designSystem: null }` so the iframe still boots
 * with Figma defaults.
 */
export async function getDesignSystem(client: ApiClient): Promise<DesignSystemResult> {
  try {
    // Forward `?template=` from the iframe's own URL so staging environments
    // can serve the correct template variant without relying on Referer
    // headers. The production backend ignores unknown query params, so this
    // is safe in every environment.
    const iframeTemplate =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('template')
        : null;
    const path = iframeTemplate
      ? `/loot_machine/api/game_platform/iframe/v1/design_system?template=${encodeURIComponent(iframeTemplate)}`
      : '/loot_machine/api/game_platform/iframe/v1/design_system';
    const raw = await client.request<RawResponse>(path, { method: 'GET' });
    return {
      template: normalizeTemplate(raw?.template),
      designSystem: parseConstructorV2(raw?.design_system),
      animation: parseOpeningAnimation(raw?.design_system),
      disableShipping: raw?.disable_shipping === true,
      fundingMode: parseFundingMode(raw?.funding_mode),
      allowWithdrawals: raw?.allow_withdrawals === true,
      registration: parseRegistration(raw?.registration),
      balanceKeepaliveSec: parseKeepaliveSec(raw?.balance_keepalive_interval_sec),
      lobbyUrl: parseHttpUrl(raw?.lobby_url),
      depositUrl: parseHttpUrl(raw?.deposit_url),
      demoEmbedKey:
        typeof raw?.demo_embed_key === 'string' && raw.demo_embed_key.length > 0
          ? raw.demo_embed_key
          : null,
      provablyFair: raw?.provably_fair === true,
      trackingPixels: parseTrackingConfig(raw?.tracking_pixels),
    };
  } catch {
    return {
      template: normalizeTemplate(null),
      designSystem: null,
      animation: DEFAULT_ANIMATION,
      disableShipping: false,
      fundingMode: 'seamless',
      allowWithdrawals: false,
      registration: { mode: 'disabled', registered: true },
      balanceKeepaliveSec: 0,
      lobbyUrl: null,
      depositUrl: null,
      demoEmbedKey: null,
      provablyFair: false,
      trackingPixels: null,
    };
  }
}
